import * as React from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import {
  APPENDIX_MAIN_FIELD_TEXT,
  createEmptyAppendixRegistryRow,
  sanitizeAppendixDocNo,
  sanitizeAppendixSheets,
  type AppendixRegistryRow
} from './appendixRegistryHelpers';

interface AppendixRegistryDialogProps {
  visible: boolean;
  rows: AppendixRegistryRow[];
  setRows: (next: AppendixRegistryRow[]) => void;
  onHide: () => void;
  onApply: () => void;
}

export const AppendixRegistryDialog: React.FC<AppendixRegistryDialogProps> = (props) => {
  const { visible, rows, setRows, onHide, onApply } = props;

  const footer = (
    <div className="d-flex justify-content-end" style={{ gap: '0.5rem' }}>
      <Button label="Отмена" icon="pi pi-times" severity="secondary" onClick={onHide} />
      <Button label="Применить" icon="pi pi-check" onClick={onApply} />
    </div>
  );

  const patchRow = (idx: number, patch: Partial<AppendixRegistryRow>) => {
    const next = [...rows];
    next[idx] = { ...next[idx], ...patch };
    setRows(next);
  };

  const addRow = () => setRows([...rows, createEmptyAppendixRegistryRow()]);

  const removeRow = (idx: number) => {
    const next = rows.filter((_, i) => i !== idx);
    setRows(next.length ? next : [createEmptyAppendixRegistryRow()]);
  };

  const duplicateRow = (idx: number) => {
    const next = [...rows];
    next.splice(idx + 1, 0, { ...rows[idx] });
    setRows(next);
  };

  return (
    <Dialog
      header="Реестр приложений № 1 — строки таблицы"
      visible={visible}
      style={{ width: '960px', maxWidth: '98vw' }}
      modal
      onHide={onHide}
      footer={footer}
    >
      <div className="mb-3">
        <Button type="button" label="Добавить строку" icon="pi pi-plus" size="small" onClick={addRow} />
      </div>
      <div>
        <table className="table table-sm table-bordered">
          <thead className="thead-light">
            <tr>
              <th style={{ textAlign: 'center', width: '3em' }}>№ п/п</th>
              <th style={{ textAlign: 'center' }}>Наименование материала</th>
              <th style={{ textAlign: 'center' }}>Наименование документа</th>
              <th style={{ textAlign: 'center' }}>№ документа</th>
              <th style={{ textAlign: 'center' }}>Дата документа</th>
              <th style={{ textAlign: 'center' }}>Кол-во листов</th>
              <th style={{ textAlign: 'center' }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={idx}>
                <td className="align-middle text-center">{idx + 1}</td>
                <td>
                  <InputText
                    value={r.material}
                    onChange={(e) => patchRow(idx, { material: e.target.value })}
                    className="w-100"
                  />
                </td>
                <td>
                  <InputText
                    value={r.docName}
                    onChange={(e) => patchRow(idx, { docName: e.target.value })}
                    className="w-100"
                  />
                </td>
                <td>
                  <InputText
                    value={r.docNo}
                    onChange={(e) => patchRow(idx, { docNo: sanitizeAppendixDocNo(e.target.value) })}
                    className="w-100"
                    placeholder="33-44"
                  />
                </td>
                <td>
                  <InputText
                    value={r.docDate}
                    onChange={(e) => patchRow(idx, { docDate: e.target.value })}
                    className="w-100"
                  />
                </td>
                <td>
                  <InputText
                    value={r.sheets}
                    onChange={(e) => patchRow(idx, { sheets: sanitizeAppendixSheets(e.target.value) })}
                    className="w-100"
                    inputMode="numeric"
                    placeholder="1"
                  />
                </td>
                <td className="text-center align-middle">
                  <div className="d-flex justify-content-center" style={{ gap: '0.25rem' }}>
                    <Button
                      type="button"
                      icon="pi pi-copy"
                      rounded
                      text
                      severity="secondary"
                      onClick={() => duplicateRow(idx)}
                      aria-label="Копировать строку"
                    />
                    <Button
                      type="button"
                      icon="pi pi-trash"
                      rounded
                      text
                      severity="danger"
                      disabled={rows.length <= 1}
                      onClick={() => removeRow(idx)}
                      aria-label="Удалить строку"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-muted mb-0" style={{ fontSize: '12px' }}>
        Если строк больше пяти, в основном тексте акта подставится формулировка "{APPENDIX_MAIN_FIELD_TEXT}", а таблица
        будет выведена в приложении к акту.
      </p>
    </Dialog>
  );
};
